import { StoryblokComponent } from "gatsby-source-storyblok"
import React, { PropsWithChildren } from "react"

import Gallery from "./Gallery"
import type { GalleryProps } from "./Gallery"
import { PayflexSection } from "./Payflex/PayflexSection"
import LayoutItem from "../../Layouts/LayoutItem"

import Accordion from "@components/elements/V2/Accordion"
import TextButton from "@components/elements/V2/Buttons/TextButton"
import DeliveryIcon from "@components/elements/V2/Icons/DeliveryIcon"
import PriceDisplay from "@components/elements/V2/PriceDisplay"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import "./styles.css"

type Props = {
  name: string
  description?: string
  productUrl: string
  superscript?: string
  deliveryTitle?: string
  deliveryDescription?: string
  price: string
  originalPrice?: string
  priceSubscriptText?: string
  promotionStartDate?: string
  promotionEndDate?: string
  selectionContent: React.ReactNode
  sideSelectionContent?: React.ReactNode
  middleContent?: React.ReactNode
  footerContent?: React.ReactNode
  contentSections: Storyblok.InfoSection[]
  originalPriceLabel?: string
  originalPriceColor?: Color
  overrideShowDiscount?: boolean
  showPayflexSection?: boolean
  mobileGridOrder?: number
} & GalleryProps

/**
 * Shared info section component used for Products and Solutions
 *
 * Splits logic-specific fragments into `content` props
 */
const InfoSection = ({
  name,
  description,
  productUrl,
  superscript,
  deliveryTitle,
  deliveryDescription,
  galleryItems,
  price,
  originalPrice,
  priceSubscriptText,
  promotionStartDate,
  promotionEndDate,
  originalPriceLabel = "WAS",
  originalPriceColor,
  selectionContent,
  middleContent,
  footerContent,
  contentSections,
  sideSelectionContent,
  overrideShowDiscount,
  showPayflexSection,
  mobileGridOrder,
}: Props) => {
  return (
    <>
      <LayoutItem
        mobileGridOrder={mobileGridOrder}
        as="div"
        className="relative col-span-4 mt-20 self-start max-md:-mx-gutter-sm max-sm:!-mx-gutter-xs md:sticky md:top-[92px] md:col-span-3 md:mt-0 lg:col-span-6"
      >
        <div className="pb-12 md:pb-16">
          <TextButton
            style="text"
            text="YOCO SHOP"
            textSize="subscript-sm"
            linkUrl={`${
              process.env.GATSBY_PREFIX_PATH || ""
            }/shop/card-machines`}
            color={Color.Black}
            className="pl-gutter-xs pr-10 opacity-70 md:pl-0"
          />
          <TextButton
            style="text"
            text={name}
            textSize="subscript-sm"
            linkUrl={productUrl}
            color={Color.Black}
          />
        </div>
        <Gallery galleryItems={galleryItems} />
      </LayoutItem>
      <LayoutItem
        mobileGridOrder={mobileGridOrder}
        as="div"
        className="relative col-span-4 pt-20 sm:py-10 md:col-span-3 md:pt-64 lg:col-span-6 lg:pt-32"
      >
        <InfoSectionLayout>
          {superscript && (
            <Typography
              text={superscript}
              font="grotesk"
              color={Color.Green}
              size="subscript-md"
              weight="medium"
            />
          )}
          <Typography
            text={name}
            font="grotesk"
            color={Color.Charcoal}
            size="h4"
            weight="medium"
            element="h1"
          />

          <Typography
            text={description || "No product description available"}
            font="grotesk"
            color={Color.Charcoal}
            size="body-md"
            weight="book"
          />
        </InfoSectionLayout>
        <InfoSectionLayout>
          <div className="flex items-center justify-between">
            <PriceDisplay
              productPrices={{
                price,
                originalPrice,
              }}
              subscriptText={priceSubscriptText}
              priceFontSize="lead-lg"
              priceFontWeight="medium"
              superscriptFontsize="body-sm"
              overrideShowDiscount={overrideShowDiscount}
              promotionStartDate={promotionStartDate}
              promotionEndDate={promotionEndDate}
              originalPriceLabel={originalPriceLabel}
              className="lg:items-start"
              originalPriceColor={originalPriceColor}
            />
            {sideSelectionContent}
          </div>
          {showPayflexSection && <PayflexSection price={parseFloat(price)} />}
          {selectionContent}
          {deliveryTitle && deliveryDescription && (
            <div className="mb-16 mt-16 flex gap-16 rounded-16 border border-charcoal border-opacity-10 p-16 lg:gap-20">
              <div className="mt-[2px]">
                <DeliveryIcon />
              </div>
              <div className="flex flex-col gap-4">
                <Typography
                  text={deliveryTitle}
                  font="grotesk"
                  color={Color.Charcoal}
                  size="body-md"
                  weight="medium"
                />
                <Typography
                  text={deliveryDescription}
                  font="grotesk"
                  color={Color.Charcoal}
                  size="body-md"
                  weight="book"
                />
              </div>
            </div>
          )}
        </InfoSectionLayout>

        {middleContent}
        {contentSections?.map((section, index) => (
          <div key={index}>
            {index > 0 && <hr className="border-gray my-16 opacity-10" />}
            <Accordion
              title={section.title}
              textColor={Color.Black}
              iconColor={Color.Black}
              contentLeftPadding={section.contentLeftPadding}
            >
              <div>
                {section.subTitle && (
                  <Typography
                    text={section.subTitle}
                    font="grotesk"
                    color={Color.Charcoal}
                    size="body-md"
                    weight="medium"
                    className="pb-16"
                  />
                )}
                {section.description && (
                  <Typography
                    text={section.description}
                    font="grotesk"
                    color={Color.Charcoal}
                    size="body-md"
                    weight="book"
                    className="pb-16"
                  />
                )}
                {section.content?.map((content) => (
                  <div key={content._uid} className="py-4">
                    <StoryblokComponent blok={content} className="!flex-row" />
                  </div>
                ))}
              </div>
            </Accordion>
          </div>
        ))}
        {footerContent}
      </LayoutItem>
    </>
  )
}
function InfoSectionLayout({ children }: PropsWithChildren) {
  return (
    <div className="py-xs flex flex-col gap-y-8 lg:gap-y-16">{children}</div>
  )
}

export default InfoSection
